import React from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  IconButton,
  useToast,
  Switch,
  NumberInput,
  NumberInputField,
  Image,
  Divider,
  Avatar,
  Progress,
  useColorModeValue,
  FormControl,
  FormLabel,
  FormHelperText,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Checkbox,
  CheckboxGroup,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminListRewards,
  adminCreateReward,
  adminUpdateReward,
  adminDeleteReward,
  adminListRedemptions,
  adminUpdateRedemptionStatus,
  adminGetLeaderboard,
  adminListTransactions,
  adminAdjustPoints,
  AdminRewardItem,
  AdminRedemption,
  adminListUsers,
  type AdminUserListItem,
} from '../../services/admin/engagement';
import { FiTrash2, FiEdit2, FiCopy } from 'react-icons/fi';
import api from '../../services/api';
import { assetUrl } from '../../utils/url';
import { PasswordHelpTooltip, HelpTooltipCard } from '../../components/common/HelpTooltipCard';
import UploadPanel from '../../components/common/UploadPanel';

// Quick presets for sizes and colors
const SIZE_OPTIONS = ['XS','S','M','L','XL','XXL','XXXL','UNI'];
const COLOR_OPTIONS = ['Černá','Bílá','Modrá','Červená','Zelená','Žlutá','Oranžová','Fialová','Šedá','Růžová','Hnědá','Navy','Béžová','Tyrkysová','Vínová'];

const EngagementAdminPage: React.FC = () => {
  const toast = useToast();
  const qc = useQueryClient();
  const cardBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');

  const [rewardFilter, setRewardFilter] = React.useState<'all'|'active'|'inactive'>('all');
  const rewardsQ = useQuery({
    queryKey: ['admin-engagement-rewards', rewardFilter],
    queryFn: () => rewardFilter === 'all' ? adminListRewards() : adminListRewards({ active: rewardFilter === 'active' }),
  });
  const redemptionsQ = useQuery({
    queryKey: ['admin-engagement-redemptions'],
    queryFn: () => adminListRedemptions(),
  });

  const [form, setForm] = React.useState({
    name: '',
    type: 'merch_digital',
    cost_points: 50,
    image_url: '',
    stock: -1,
    active: true,
  });

  // Create form helpers
  const [validUnlimited, setValidUnlimited] = React.useState<boolean>(true);
  const [sizeList, setSizeList] = React.useState<string[]>([]);
  const [colorList, setColorList] = React.useState<string[]>([]);
  const [sizeCustom, setSizeCustom] = React.useState<string>('');
  const [colorCustom, setColorCustom] = React.useState<string>('');

  const [editItem, setEditItem] = React.useState<AdminRewardItem | null>(null);
  const editModal = useDisclosure();
  const [editForm, setEditForm] = React.useState<Partial<AdminRewardItem>>({});
  // Remove raw JSON editing, keep structured metadata only

  // Structured metadata state (used for merch types, coupons, etc.)
  const [meta, setMeta] = React.useState<Record<string, any>>({});
  const [editMeta, setEditMeta] = React.useState<Record<string, any>>({});

  // Users list for dropdowns and labels
  const usersQ = useQuery({
    queryKey: ['admin-users'],
    queryFn: adminListUsers,
    staleTime: 30000,
  });
  const usersById = React.useMemo(() => {
    const m = new Map<number, AdminUserListItem>();
    (usersQ.data || []).forEach((u) => m.set(u.id, u));
    return m;
  }, [usersQ.data]);

  // Removed reward templates UI

  const setMetaField = (k: string, v: string) => {
    const next = { ...meta, [k]: v };
    setMeta(next);
  };
  const setEditMetaField = (k: string, v: string) => {
    const next = { ...editMeta, [k]: v };
    setEditMeta(next);
  };

  const createMut = useMutation({
    mutationFn: async () => {
      // Build metadata including structured helpers
      const md: Record<string, any> = { ...(Object.keys(meta).length ? meta : {}) };
      if (validUnlimited) { delete md.valid_from; delete md.valid_to; }
      if (sizeList.length) md.size = sizeList.join(',');
      if (colorList.length) md.color = colorList.join(',');
      const metadata = Object.keys(md).length ? md : undefined;
      return adminCreateReward({ ...form, metadata });
    },
    onSuccess: async () => {
      setForm({ name: '', type: 'merch_digital', cost_points: 50, image_url: '', stock: -1, active: true });
      setValidUnlimited(true);
      setSizeList([]); setColorList([]); setSizeCustom(''); setColorCustom(''); setMeta({});
      await qc.invalidateQueries({ queryKey: ['admin-engagement-rewards'] });
      toast({ status: 'success', title: 'Odměna vytvořena' });
    },
    onError: (e: any) => toast({ status: 'error', title: e?.message || e?.response?.data?.error || 'Chyba při vytváření odměny' }),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: number; body: Partial<AdminRewardItem> }) => adminUpdateReward(args.id, args.body as any),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['admin-engagement-rewards'] }); toast({ status: 'success', title: 'Aktualizováno' }); },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => adminDeleteReward(id),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['admin-engagement-rewards'] }); toast({ status: 'success', title: 'Smazáno' }); },
  });

  const redStatusMut = useMutation({
    mutationFn: (args: { id: number; action: 'approve'|'reject'|'fulfill' }) => adminUpdateRedemptionStatus(args.id, args.action),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['admin-engagement-redemptions'] }); toast({ status: 'success', title: 'Status aktualizován' }); },
  });

  

  const rewards = rewardsQ.data || [];
  const redemptions = redemptionsQ.data || [];
  const [metric, setMetric] = React.useState<'points'|'level'|'xp'>('points');
  const [leaders, setLeaders] = React.useState<any[]>([]);
  const [loadingLb, setLoadingLb] = React.useState(false);

  const rewardById = React.useMemo(() => {
    const m = new Map<number, AdminRewardItem>();
    for (const r of rewards) m.set(r.id as any, r);
    return m;
  }, [rewards]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingLb(true);
        const res = await adminGetLeaderboard(metric, 50);
        if (mounted) setLeaders(res.items || []);
      } catch {
        if (mounted) setLeaders([]);
      } finally {
        if (mounted) setLoadingLb(false);
      }
    })();
    return () => { mounted = false; };
  }, [metric]);

  return (
    <AdminLayout>
      <Box>
        <Heading size="md" mb={4}>Odměny & Úspěchy</Heading>
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" mb={2}>Žebříčky</Heading>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm" color="gray.500">Top uživatelé podle zvoleného metrického ukazatele</Text>
              <Select size="sm" value={metric} onChange={(e)=>setMetric(e.target.value as any)} maxW="180px">
                <option value="points">Body</option>
                <option value="level">Úroveň</option>
                <option value="xp">XP</option>
              </Select>
            </HStack>
            <Box borderWidth="1px" borderRadius="md" borderColor={border} bg={cardBg} p={3}>
              <VStack align="stretch" spacing={2}>
                {loadingLb && <Text>Načítám…</Text>}
                {!loadingLb && leaders.length === 0 && <Text>Žádná data k zobrazení.</Text>}
                {!loadingLb && leaders.map((it: any) => {
                  const value = metric === 'points' ? it.points : (metric === 'level' ? it.level : it.xp);
                  const max = Math.max(...leaders.map((l: any) => metric === 'points' ? l.points : (metric === 'level' ? l.level : l.xp)), 1);
                  const pct = Math.max(2, Math.floor((Number(value) / Number(max)) * 100));
                  const name = `${it.first_name || ''} ${it.last_name || ''}`.trim() || it.email || `#${it.user_id}`;
                  return (
                    <HStack key={`lb-${metric}-${it.user_id}`} spacing={3}>
                      <Badge colorScheme="blue">{it.rank}</Badge>
                      <Avatar size="sm" name={name} src={it.animated_avatar_url || it.avatar_url || undefined} />
                      <Box flex={1}>
                        <HStack justify="space-between">
                          <Text fontWeight="600" noOfLines={1}>{name}</Text>
                          <Text fontSize="sm">{value}</Text>
                        </HStack>
                        <Progress value={pct} size="sm" colorScheme="blue" borderRadius="full" mt={1} />
                      </Box>
                    </HStack>
                  );
                })}
              </VStack>
            </Box>
          </Box>
          <Box>
            <HStack justify="space-between" align="center" mb={2}>
              <Heading size="sm">Vytvořit novou odměnu</Heading>
              <HelpTooltipCard
                label="Jak nastavit odměnu"
                title="Vytvoření odměny pro věrnostní systém"
                items={[
                  'Každá odměna má typ (digitální, kupon, fyzický merch, vlastní), který určuje, jaké informace je potřeba vyplnit a co se stane po uplatnění.',
                  'Pole Množství/Sklad používá hodnotu -1 pro neomezený počet kusů a 0 pro vyprodáno; u povinných avatarových odměn je sklad vždy neomezený.',
                  'Cenu v bodech volte konzistentně s ostatními odměnami, aby měly fanoušci jasnou představu o „hodnotě“ bodů.',
                ]}
              />
            </HStack>
            <VStack align="stretch" spacing={3} borderWidth="1px" borderRadius="md" p={3}>
              {/* Šablony odměn odstraněny */}
              <HStack align="start" spacing={4}>
                <VStack align="stretch" spacing={3} flex={1}>
                  <FormControl>
                    <FormLabel>Název</FormLabel>
                    <Input placeholder="Např. Modrý avatar #1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Typ odměny</FormLabel>
                    <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                      <option value="merch_digital">Digitální odměna</option>
                      <option value="merch_coupon">Merch kupon</option>
                      <option value="merch_physical">Merch (fyzický)</option>
                      <option value="custom">Vlastní</option>
                    </Select>
                    <FormHelperText>Ovlivňuje chování po uplatnění (např. nastavení avataru).</FormHelperText>
                  </FormControl>
                  <HStack>
                    <FormControl>
                      <FormLabel>Body</FormLabel>
                      <NumberInput value={form.cost_points} min={0} onChange={(_v, n) => setForm({ ...form, cost_points: Number.isFinite(n) ? n : 0 })}>
                        <NumberInputField placeholder="Počet bodů" />
                      </NumberInput>
                      <FormHelperText>~ {Math.round(Number(form.cost_points || 0) * 0.1)} Kč</FormHelperText>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Množství/Sklad</FormLabel>
                      <HStack>
                        <NumberInput value={form.stock} min={-1} isDisabled={form.stock === -1} onChange={(_v, n) => setForm({ ...form, stock: Number.isFinite(n) ? n : -1 })}>
                          <NumberInputField placeholder="Ks (-1 = neomezeně, 0 = vyprodáno)" />
                        </NumberInput>
                        <HStack>
                          <Text fontSize="sm">Neomezeně</Text>
                          <Switch
                            isChecked={form.stock === -1}
                            onChange={(e)=> setForm(prev => ({
                              ...prev,
                              stock: e.target.checked ? -1 : Math.max(0, Number(prev.stock) === -1 ? 0 : Number(prev.stock)||0),
                            }))}
                          />
                        </HStack>
                      </HStack>
                    </FormControl>
                  </HStack>
                  <VStack align="stretch" spacing={2} mt={2}>
                    <HStack>
                      <Text>Neomezená platnost</Text>
                      <Switch
                        isChecked={validUnlimited}
                        onChange={(e)=>{
                          const on = e.target.checked;
                          setValidUnlimited(on);
                          if (on) { setMetaField('valid_from', ''); setMetaField('valid_to', ''); }
                        }}
                      />
                    </HStack>
                    <FormControl isDisabled={validUnlimited}>
                      <FormLabel>Platnost od</FormLabel>
                      <Input type="datetime-local" value={meta.valid_from || ''} onChange={(e)=>setMetaField('valid_from', e.target.value)} />
                    </FormControl>
                    <FormControl isDisabled={validUnlimited}>
                      <FormLabel>Platnost do</FormLabel>
                      <Input type="datetime-local" value={meta.valid_to || ''} onChange={(e)=>setMetaField('valid_to', e.target.value)} />
                      <FormHelperText>Když je zapnuto „Neomezená platnost“, datumy se nevyžadují a ignorují.</FormHelperText>
                    </FormControl>
                  </VStack>
                  {/* Metadata helpers */}
                  {form.type === 'merch_coupon' && (
                    <VStack align="stretch" spacing={2}>
                      <FormControl>
                        <FormLabel>Kód kuponu</FormLabel>
                        <Input value={meta.coupon_code || ''} onChange={(e)=>setMetaField('coupon_code', e.target.value)} />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Poznámka</FormLabel>
                        <Input value={meta.note || ''} onChange={(e)=>setMetaField('note', e.target.value)} />
                      </FormControl>
                    </VStack>
                  )}
                  {form.type === 'merch_physical' && (
                    <VStack align="stretch" spacing={2}>
                      <FormControl>
                        <FormLabel>Velikosti</FormLabel>
                        <CheckboxGroup value={sizeList} onChange={(vals)=>setSizeList(vals as string[])}>
                          <Wrap spacing={3}>
                            {SIZE_OPTIONS.map((s)=> (
                              <WrapItem key={s}><Checkbox value={s}>{s}</Checkbox></WrapItem>
                            ))}
                          </Wrap>
                        </CheckboxGroup>
                        <HStack mt={1} spacing={2}>
                          <Input placeholder="Vlastní velikosti, oddělte čárkami" value={sizeCustom} onChange={(e)=>setSizeCustom(e.target.value)} />
                          <Button size="sm" onClick={()=>{
                            const parts = (sizeCustom || '').split(',').map(s=>s.trim()).filter(Boolean);
                            if (!parts.length) return;
                            setSizeList(prev => Array.from(new Set([...prev, ...parts])));
                            setSizeCustom('');
                          }}>Přidat</Button>
                        </HStack>
                        <FormHelperText>Vyberte z nabídky nebo zadejte vlastní hodnoty, oddělené čárkami (např. 122, 128).</FormHelperText>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Barvy</FormLabel>
                        <CheckboxGroup value={colorList} onChange={(vals)=>setColorList(vals as string[])}>
                          <Wrap spacing={3}>
                            {COLOR_OPTIONS.map((c)=> (
                              <WrapItem key={c}><Checkbox value={c}>{c}</Checkbox></WrapItem>
                            ))}
                          </Wrap>
                        </CheckboxGroup>
                        <HStack mt={1} spacing={2}>
                          <Input placeholder="Vlastní barvy, oddělte čárkami" value={colorCustom} onChange={(e)=>setColorCustom(e.target.value)} />
                          <Button size="sm" onClick={()=>{
                            const parts = (colorCustom || '').split(',').map(s=>s.trim()).filter(Boolean);
                            if (!parts.length) return;
                            setColorList(prev => Array.from(new Set([...prev, ...parts])));
                            setColorCustom('');
                          }}>Přidat</Button>
                        </HStack>
                        <FormHelperText>Vyberte více možností nebo přidejte vlastní barvy (oddělené čárkami).</FormHelperText>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Poznámka</FormLabel>
                        <Input value={meta.note || ''} onChange={(e)=>setMetaField('note', e.target.value)} />
                      </FormControl>
                    </VStack>
                  )}
                  {form.type === 'merch_digital' && (
                    <VStack align="stretch" spacing={2}>
                      <FormControl><FormLabel>Licenční klíč</FormLabel><Input value={meta.license_key || ''} onChange={(e)=>setMetaField('license_key', e.target.value)} /></FormControl>
                      <FormControl><FormLabel>Stažení (URL)</FormLabel><Input value={meta.download_url || ''} onChange={(e)=>setMetaField('download_url', e.target.value)} /></FormControl>
                      <FormControl><FormLabel>Poznámka</FormLabel><Input value={meta.note || ''} onChange={(e)=>setMetaField('note', e.target.value)} /></FormControl>
                    </VStack>
                  )}
                  {form.type === 'custom' && (
                    <VStack align="stretch" spacing={2}>
                      <HStack>
                        <Input placeholder="klíč" id="kv-key" />
                        <Input placeholder="hodnota" id="kv-value" />
                        <Button size="sm" onClick={()=>{
                          const k = (document.getElementById('kv-key') as HTMLInputElement)?.value?.trim();
                          const v = (document.getElementById('kv-value') as HTMLInputElement)?.value?.trim();
                          if (!k) return;
                          setMetaField(k, v || '');
                        }}>Přidat</Button>
                      </HStack>
                    </VStack>
                  )}
                  {/* Odstraněno: ruční JSON metadata. Metadata se vyplňují automaticky z polí výše. */}
                  <HStack>
                    <Text>Aktivní</Text>
                    <Switch isChecked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                    <Button colorScheme="blue" onClick={() => createMut.mutate()} isLoading={createMut.isPending} isDisabled={!form.name.trim()}>Vytvořit</Button>
                  </HStack>
                </VStack>
                <Box>
                  <Text fontSize="sm" mb={2} color="gray.500">Náhled</Text>
                  <Box borderWidth="1px" borderRadius="md" p={2}>
                    {form.image_url ? (
                      <Image src={assetUrl(form.image_url)} alt={form.name} boxSize="96px" objectFit="cover" borderRadius="md" />
                    ) : (
                      <Box boxSize="96px" borderWidth="1px" borderRadius="md" display="flex" alignItems="center" justifyContent="center" color="gray.400">Bez obrázku</Box>
                    )}
                  </Box>
                </Box>
              </HStack>
            </VStack>
          </Box>

          <Divider />

          <Box>
            <Heading size="sm" mb={2}>Odměny</Heading>
            <HStack mb={2}>
              <Text fontSize="sm" color="gray.500">Filtrovat:</Text>
              <Select size="sm" value={rewardFilter} onChange={(e)=>setRewardFilter(e.target.value as any)} maxW="200px">
                <option value="all">Vše</option>
                <option value="active">Pouze aktivní</option>
                <option value="inactive">Pouze neaktivní</option>
              </Select>
            </HStack>
            <Box borderWidth="1px" borderRadius="md" overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>ID</Th>
                    <Th>Název</Th>
                    <Th>Typ</Th>
                    <Th>Body</Th>
                    <Th>Množství/Sklad</Th>
                    <Th>Obrázek</Th>
                    <Th>Platnost</Th>
                    <Th>Aktivní</Th>
                    <Th>Akce</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rewards.map((r: AdminRewardItem) => (
                    <Tr key={r.id}>
                      <Td>#{r.id}</Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text>{r.name}</Text>
                          {r.type === 'merch_physical' && (r.metadata as any)?.sku ? (
                            <HStack spacing={1}>
                              <Text fontSize="xs" color="gray.500">SKU: {(r.metadata as any).sku}</Text>
                              <IconButton aria-label="Kopírovat SKU" size="xs" variant="ghost" icon={<FiCopy />} onClick={()=> navigator.clipboard?.writeText(String((r.metadata as any).sku || ''))} />
                            </HStack>
                          ) : null}
                        </VStack>
                      </Td>
                      <Td><Badge>{r.type}</Badge></Td>
                      <Td>
                        <NumberInput size="sm" value={r.cost_points} min={0} maxW="120px" isDisabled={!!r.type && r.type.startsWith('avatar_')} onChange={(_v, n) => updateMut.mutate({ id: r.id, body: { cost_points: Number.isFinite(n) ? n : 0 } })}>
                          <NumberInputField />
                        </NumberInput>
                      </Td>
                      <Td>
                        <NumberInput
                          size="sm"
                          value={r.stock ?? 0}
                          min={-1}
                          maxW="100px"
                          isDisabled={!!r.type && r.type.startsWith('avatar_')}
                          onChange={(_v, n) => updateMut.mutate({ id: r.id, body: { stock: Number.isFinite(n) ? n : 0 } })}
                        >
                          <NumberInputField />
                        </NumberInput>
                      </Td>
                      <Td>{r.image_url ? <Image src={assetUrl(r.image_url)} alt={r.name} boxSize="40px" objectFit="cover" borderRadius="md" /> : '-'}</Td>
                      <Td>
                        {(() => {
                          const m = (r.metadata || {}) as any;
                          const vf = m.valid_from ? new Date(m.valid_from) : null;
                          const vt = m.valid_to ? new Date(m.valid_to) : null;
                          if (!vf && !vt) return <Text color="gray.500">-</Text>;
                          return (
                            <VStack align="start" spacing={0}>
                              {vf && <Text fontSize="xs">od {vf.toLocaleString()}</Text>}
                              {vt && <Text fontSize="xs">do {vt.toLocaleString()}</Text>}
                            </VStack>
                          );
                        })()}
                      </Td>
                      <Td>
                        <Switch
                          isChecked={!!r.active}
                          isDisabled={!!r.type && r.type.startsWith('avatar_')}
                          onChange={(e) => updateMut.mutate({ id: r.id, body: { active: e.target.checked } })}
                        />
                      </Td>
                      <Td>
                        <HStack>
                          <IconButton aria-label="Upravit" size="xs" icon={<FiEdit2 />} onClick={() => { 
                            setEditItem(r); 
                            setEditForm(r); 
                            const m: any = r.metadata || {}; 
                            const prepared: any = { ...m }; 
                            try {
                              if (typeof m.size === 'string') prepared.__size_list = String(m.size).split(',').map((s:string)=>s.trim()).filter(Boolean);
                            } catch {}
                            try {
                              if (typeof m.color === 'string') prepared.__color_list = String(m.color).split(',').map((s:string)=>s.trim()).filter(Boolean);
                            } catch {}
                            setEditMeta(prepared); 
                            editModal.onOpen(); 
                          }} />
                          {!r.type?.startsWith('avatar_') && (
                            <IconButton aria-label="Smazat" size="xs" icon={<FiTrash2 />} onClick={() => deleteMut.mutate(r.id)} />
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>

          <Box>
            <HStack justify="space-between" align="center" mt={6} mb={2}>
              <Heading size="sm">Uplatnění odměn</Heading>
              <HelpTooltipCard
                label="Jak pracovat s uplatněním"
                title="Vyřízení požadavků na odměny"
                items={[
                  'Zde vidíte požadavky uživatelů na vydání odměn – každý záznam odpovídá konkrétní odměně a uživateli.',
                  'Akce Schválit/Zamítnout/Vydat pomáhají sledovat stav vyřízení (např. fyzické předání merchu nebo zaslání kuponu).',
                  'Doporučujeme, aby změny stavů prováděli pouze administrátoři – kvůli konzistenci bodů a auditovatelnosti.',
                ]}
              />
            </HStack>
            <Box borderWidth="1px" borderRadius="md" overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>ID</Th>
                    <Th>Uživatel</Th>
                    <Th>Odměna</Th>
                    <Th>Vytvořeno</Th>
                    <Th>Status</Th>
                    <Th>Akce</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {redemptions.map((d: AdminRedemption) => (
                    <Tr key={d.id}>
                      <Td>#{d.id}</Td>
                      <Td>
                        {usersById.get(d.user_id as any)?.name ? (
                          <HStack spacing={1}>
                            <Text noOfLines={1}>{usersById.get(d.user_id as any)?.name}</Text>
                            <Text color="gray.500" fontSize="xs">#{d.user_id}</Text>
                          </HStack>
                        ) : (
                          <Text>#{d.user_id}</Text>
                        )}
                      </Td>
                      <Td>
                        <HStack>
                          <Text>#{d.reward_id}</Text>
                          {rewardById.get(d.reward_id as any)?.name && <Text as="span">– {rewardById.get(d.reward_id as any)?.name}</Text>}
                          {rewardById.get(d.reward_id as any)?.type && <Badge>{rewardById.get(d.reward_id as any)?.type}</Badge>}
                        </HStack>
                      </Td>
                      <Td>{d.created_at ? new Date(d.created_at as any).toLocaleString() : '-'}</Td>
                      <Td><Badge colorScheme={d.status === 'approved' ? 'blue' : d.status === 'fulfilled' ? 'green' : d.status === 'rejected' ? 'red' : 'gray'}>{d.status}</Badge></Td>
                      <Td>
                        <HStack>
                          <Button size="xs" variant="outline" onClick={() => redStatusMut.mutate({ id: d.id, action: 'approve' })}>Schválit</Button>
                          <Button size="xs" variant="outline" colorScheme="red" onClick={() => redStatusMut.mutate({ id: d.id, action: 'reject' })}>Zamítnout</Button>
                          <Button size="xs" variant="outline" colorScheme="green" onClick={() => redStatusMut.mutate({ id: d.id, action: 'fulfill' })}>Vydat</Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {/* Transactions & Adjust */}
          <Box>
            <HStack justify="space-between" align="center" mt={6} mb={2}>
              <Heading size="sm">Transakce bodů & Úpravy</Heading>
              <HelpTooltipCard
                label="Jak pracovat s body"
                title="Transakce bodů a ruční úpravy"
                items={[
                  'Transakce ukazují historii pohybů bodů (získané, utracené, ručně upravené) pro jednotlivé uživatele.',
                  'Ruční úpravy bodů používejte výjimečně – například k opravě chyb nebo speciálním odměnám mimo běžná pravidla.',
                  'Pro dlouhodobou konzistenci je vhodné mít v týmu jasná pravidla, kdo může body upravovat a za jakých podmínek.',
                ]}
              />
            </HStack>
            <TransactionsAndAdjust />
          </Box>
        </VStack>
      </Box>

      {/* Edit reward modal */}
      <Modal isOpen={editModal.isOpen} onClose={editModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upravit odměnu #{editItem?.id}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Název</FormLabel>
                <Input value={editForm.name || ''} onChange={(e)=>setEditForm({ ...editForm, name: e.target.value })} isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} />
              </FormControl>
              <FormControl>
                <FormLabel>Typ</FormLabel>
                <Select value={editForm.type || ''} onChange={(e)=>setEditForm({ ...editForm, type: e.target.value })} isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')}>
                  <option value="merch_digital">Digitální odměna</option>
                  <option value="merch_coupon">Merch kupon</option>
                  <option value="merch_physical">Merch (fyzický)</option>
                  <option value="custom">Vlastní</option>
                </Select>
              </FormControl>
              <HStack>
                <FormControl>
                  <FormLabel>Body</FormLabel>
                  <NumberInput value={Number(editForm.cost_points || 0)} min={0} onChange={(_v, n)=>setEditForm({ ...editForm, cost_points: Number.isFinite(n)? n : 0 })} isDisabled={!editItem || (!!editItem.type && !editItem.type.startsWith('avatar_') && false)}>
                    <NumberInputField />
                  </NumberInput>
                  <FormHelperText>~ {Math.round(Number(editForm.cost_points || 0) * 0.1)} Kč</FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Množství/Sklad</FormLabel>
                  <HStack>
                    <NumberInput value={Number(editForm.stock || 0)} min={-1} isDisabled={(!!editItem?.type && editItem.type.startsWith('avatar_')) || Number(editForm.stock) === -1} onChange={(_v, n)=>setEditForm({ ...editForm, stock: Number.isFinite(n)? n : 0 })}>
                      <NumberInputField />
                    </NumberInput>
                    <HStack>
                      <Text fontSize="sm">Neomezeně</Text>
                      <Switch isChecked={Number(editForm.stock) === -1} onChange={(e)=> setEditForm(prev => ({ ...prev, stock: e.target.checked ? -1 : Math.max(0, Number(prev.stock) === -1 ? 0 : Number(prev.stock)||0) }))} isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} />
                    </HStack>
                  </HStack>
                  <FormHelperText>-1 = neomezeně, 0 = vyprodáno.</FormHelperText>
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>Obrázek URL</FormLabel>
                <Input
                  value={editForm.image_url || ''}
                  onChange={(e)=>setEditForm({ ...editForm, image_url: e.target.value })}
                  isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')}
                />
                <FormHelperText>Vložte URL z /uploads nebo nahrávací tlačítko (proxy na frontend funguje).</FormHelperText>
                {(!editItem?.type || !editItem.type.startsWith('avatar_')) && (
                  <Box mt={2}>
                    <UploadPanel
                      label="Nahrát obrázek"
                      description="Nahrajte obrázek odměny."
                      accept="image/*"
                      multiple={false}
                      maxFiles={1}
                      onUploadFinished={(uploaded) => {
                        const last = uploaded[uploaded.length - 1];
                        if (!last) return;
                        setEditForm((prev) => ({ ...prev, image_url: last.url }));
                      }}
                    />
                  </Box>
                )}
              </FormControl>
              {/* Edit metadata helpers (structured) */}
              { (editForm.type === 'merch_coupon' || editForm.type === 'merch_physical' || editForm.type === 'merch_digital' || editForm.type === 'custom') && (
                <VStack align="stretch" spacing={2}>
                  {editForm.type === 'merch_coupon' && (
                    <>
                      <FormControl><FormLabel>Kód kuponu</FormLabel><Input isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} value={(editMeta as any).coupon_code || ''} onChange={(e)=>setEditMetaField('coupon_code', e.target.value)} /></FormControl>
                      <FormControl><FormLabel>Poznámka</FormLabel><Input isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} value={(editMeta as any).note || ''} onChange={(e)=>setEditMetaField('note', e.target.value)} /></FormControl>
                    </>
                  )}
                  {editForm.type === 'merch_physical' && (
                    <>
                      {(editMeta as any)?.sku ? (
                        <HStack spacing={2}>
                          <Text fontSize="sm" color="gray.500">Kód (SKU): {(editMeta as any).sku}</Text>
                          <IconButton aria-label="Kopírovat SKU" size="xs" variant="ghost" icon={<FiCopy />} onClick={()=> navigator.clipboard?.writeText(String((editMeta as any).sku || ''))} />
                        </HStack>
                      ) : null}
                      <FormControl>
                        <FormLabel>Velikosti</FormLabel>
                        <CheckboxGroup value={(editMeta as any).__size_list || []} onChange={(vals)=>{
                          const arr = vals as string[];
                          setEditMeta(prev => ({ ...(prev as any), __size_list: arr } as any));
                        }}>
                          <Wrap spacing={3}>
                            {SIZE_OPTIONS.map((s)=> (
                              <WrapItem key={s}><Checkbox value={s} isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')}>{s}</Checkbox></WrapItem>
                            ))}
                          </Wrap>
                        </CheckboxGroup>
                        <HStack mt={1} spacing={2}>
                          <Input placeholder="Vlastní velikosti, oddělte čárkami" isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} value={(editMeta as any).__size_custom || ''} onChange={(e)=>setEditMetaField('__size_custom', e.target.value)} />
                          <Button size="sm" isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} onClick={()=>{
                            const parts = String((editMeta as any).__size_custom || '').split(',').map((s:string)=>s.trim()).filter(Boolean);
                            const cur = Array.isArray((editMeta as any).__size_list) ? (editMeta as any).__size_list as string[] : [];
                            const merged = Array.from(new Set([...(cur as string[]), ...parts]));
                            setEditMeta(prev => ({ ...(prev as any), __size_list: merged, __size_custom: '' } as any));
                          }}>Přidat</Button>
                        </HStack>
                        <FormHelperText>Vyberte z nabídky nebo přidejte vlastní hodnoty (oddělené čárkami).</FormHelperText>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Barvy</FormLabel>
                        <CheckboxGroup value={(editMeta as any).__color_list || []} onChange={(vals)=>{
                          const arr = vals as string[];
                          setEditMeta(prev => ({ ...(prev as any), __color_list: arr } as any));
                        }}>
                          <Wrap spacing={3}>
                            {COLOR_OPTIONS.map((c)=> (
                              <WrapItem key={c}><Checkbox value={c} isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')}>{c}</Checkbox></WrapItem>
                            ))}
                          </Wrap>
                        </CheckboxGroup>
                        <HStack mt={1} spacing={2}>
                          <Input placeholder="Vlastní barvy, oddělte čárkami" isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} value={(editMeta as any).__color_custom || ''} onChange={(e)=>setEditMetaField('__color_custom', e.target.value)} />
                          <Button size="sm" isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} onClick={()=>{
                            const parts = String((editMeta as any).__color_custom || '').split(',').map((s:string)=>s.trim()).filter(Boolean);
                            const cur = Array.isArray((editMeta as any).__color_list) ? (editMeta as any).__color_list as string[] : [];
                            const merged = Array.from(new Set([...(cur as string[]), ...parts]));
                            setEditMeta(prev => ({ ...(prev as any), __color_list: merged, __color_custom: '' } as any));
                          }}>Přidat</Button>
                        </HStack>
                        <FormHelperText>Vyberte více možností nebo přidejte vlastní barvy (oddělené čárkami).</FormHelperText>
                      </FormControl>
                      <FormControl><FormLabel>Poznámka</FormLabel><Input isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} value={(editMeta as any).note || ''} onChange={(e)=>setEditMetaField('note', e.target.value)} /></FormControl>
                    </>
                  )}
                  {editForm.type === 'merch_digital' && (
                    <>
                      <FormControl><FormLabel>Licenční klíč</FormLabel><Input isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} value={(editMeta as any).license_key || ''} onChange={(e)=>setEditMetaField('license_key', e.target.value)} /></FormControl>
                      <FormControl><FormLabel>Stažení (URL)</FormLabel><Input isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} value={(editMeta as any).download_url || ''} onChange={(e)=>setEditMetaField('download_url', e.target.value)} /></FormControl>
                      <FormControl><FormLabel>Poznámka</FormLabel><Input isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} value={(editMeta as any).note || ''} onChange={(e)=>setEditMetaField('note', e.target.value)} /></FormControl>
                    </>
                  )}
                  {editForm.type === 'custom' && (
                    <HStack>
                      <Input placeholder="klíč" id="edit-kv-key" isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} />
                      <Input placeholder="hodnota" id="edit-kv-value" isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} />
                      <Button size="sm" isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} onClick={()=>{
                        const k = (document.getElementById('edit-kv-key') as HTMLInputElement)?.value?.trim();
                        const v = (document.getElementById('edit-kv-value') as HTMLInputElement)?.value?.trim();
                        if (!k) return;
                        setEditMetaField(k, v || '');
                      }}>Přidat</Button>
                    </HStack>
                  )}
                </VStack>
              )}
              <VStack align="stretch" spacing={2}>
                <HStack>
                  <Text>Neomezená platnost</Text>
                  <Switch isChecked={!(editMeta as any).valid_from && !(editMeta as any).valid_to} onChange={(e)=>{
                    const on = e.target.checked;
                    if (on) { setEditMeta(prev => ({ ...(prev as any), valid_from: '', valid_to: '' } as any)); }
                  }} isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} />
                </HStack>
                <FormControl isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_') || (!(editMeta as any).valid_from && !(editMeta as any).valid_to)}>
                  <FormLabel>Platnost od</FormLabel>
                  <Input type="datetime-local" value={(editMeta as any).valid_from || ''} onChange={(e)=>setEditMetaField('valid_from', e.target.value)} />
                </FormControl>
                <FormControl isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_') || (!(editMeta as any).valid_from && !(editMeta as any).valid_to)}>
                  <FormLabel>Platnost do</FormLabel>
                  <Input type="datetime-local" value={(editMeta as any).valid_to || ''} onChange={(e)=>setEditMetaField('valid_to', e.target.value)} />
                  <FormHelperText>Když je zapnuto „Neomezená platnost“, datumy se nevyžadují a ignorují.</FormHelperText>
                </FormControl>
              </VStack>
              {/* Odstraněno: ruční JSON metadata v editoru. */}
              <HStack>
                <Text>Aktivní</Text>
                <Switch isChecked={!!editForm.active} onChange={(e)=>setEditForm({ ...editForm, active: e.target.checked })} isDisabled={!!editItem?.type && editItem.type.startsWith('avatar_')} />
                {editForm.image_url ? <Image src={assetUrl(String(editForm.image_url))} alt={String(editForm.name || '')} boxSize="56px" objectFit="cover" borderRadius="md" /> : null}
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button onClick={editModal.onClose}>Zrušit</Button>
              <Button colorScheme="blue" isLoading={updateMut.isPending} onClick={async ()=>{
                if (!editItem) return;
                if (editItem.type && editItem.type.startsWith('avatar_')) {
                  await updateMut.mutateAsync({ id: editItem.id, body: { cost_points: editForm.cost_points as any } });
                } else {
                  const metadata: Record<string, any> = { ...(Object.keys(editMeta || {}).length ? (editMeta as any) : {}) } as any;
                  // Merge structured lists to CSV in metadata
                  const sz = Array.isArray((metadata as any).__size_list) ? (metadata as any).__size_list as string[] : [];
                  const cz = Array.isArray((metadata as any).__color_list) ? (metadata as any).__color_list as string[] : [];
                  if (sz.length) (metadata as any).size = sz.join(',');
                  if (cz.length) (metadata as any).color = cz.join(',');
                  delete (metadata as any).__size_list; delete (metadata as any).__size_custom;
                  delete (metadata as any).__color_list; delete (metadata as any).__color_custom;
                  // If unlimited validity, clear dates
                  const unlimited = !(metadata as any).valid_from && !(metadata as any).valid_to;
                  if (unlimited) { delete (metadata as any).valid_from; delete (metadata as any).valid_to; }
                  await updateMut.mutateAsync({ id: editItem.id, body: {
                    name: editForm.name,
                    type: editForm.type,
                    cost_points: editForm.cost_points as any,
                    stock: editForm.stock as any,
                    image_url: editForm.image_url,
                    active: editForm.active as any,
                    metadata: Object.keys(metadata).length ? metadata as any : undefined,
                  } as any });
                }
                editModal.onClose();
              }}>Uložit</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      
    </AdminLayout>
  );
};

// Inline component: Transactions viewer and Adjust points panel
const TransactionsAndAdjust: React.FC = () => {
  const [userId, setUserId] = React.useState<string>('');
  const [reason, setReason] = React.useState<string>('');
  const [limit, setLimit] = React.useState<number>(100);
  const qc = useQueryClient();
  const toast = useToast();
  // Users for dropdowns
  const usersQ = useQuery({ queryKey: ['admin-users'], queryFn: adminListUsers, staleTime: 30000 });
  const txQ = useQuery({
    queryKey: ['admin-engagement-tx', { userId, reason, limit }],
    queryFn: async () => {
      const params: any = {};
      if (userId.trim()) params.user_id = userId.trim();
      if (reason.trim()) params.reason = reason.trim();
      if (limit) params.limit = limit;
      return adminListTransactions(params);
    }
  });
  const [adjUserId, setAdjUserId] = React.useState<string>('');
  const [adjDelta, setAdjDelta] = React.useState<string>('');
  const [adjReason, setAdjReason] = React.useState<string>('admin_adjust');
  const passwordModal = useDisclosure();
  const [currentPassword, setCurrentPassword] = React.useState<string>('');
  const adjustMut = useMutation({
    mutationFn: async () => {
      const uid = Number(adjUserId);
      const delta = Number(adjDelta);
      if (!uid || !delta) throw new Error('Zadejte platné user_id a delta');
      return adminAdjustPoints({ user_id: uid, delta, reason: adjReason.trim() || 'admin_adjust', current_password: currentPassword });
    },
    onSuccess: async () => {
      setAdjDelta(''); setCurrentPassword(''); passwordModal.onClose();
      await qc.invalidateQueries({ queryKey: ['admin-engagement-tx'] });
      toast({ status: 'success', title: 'Upraveno' });
    },
    onError: (e: any) => toast({ status: 'error', title: e?.message || 'Chyba při úpravě bodů' })
  });

  return (
    <VStack align="stretch" spacing={3}>
      <HStack flexWrap="wrap" rowGap={2}>
        <Select placeholder="Všichni uživatelé" value={userId} onChange={(e)=>setUserId(e.target.value)} maxW="260px">
          {(usersQ.data || []).map(u => (
            <option key={u.id} value={String(u.id)}>{u.name || u.email} (#{u.id})</option>
          ))}
        </Select>
        <Select placeholder="Důvod" value={reason} onChange={(e)=>setReason(e.target.value)} maxW="220px">
          <option value="daily_checkin">daily_checkin</option>
          <option value="article_read">article_read</option>
          <option value="redeem">redeem</option>
          <option value="redeem_refund">redeem_refund</option>
          <option value="admin_adjust">admin_adjust</option>
        </Select>
        <NumberInput value={limit} min={10} max={1000} onChange={(_v,n)=>setLimit(Number.isFinite(n)? n : 100)} maxW="160px">
          <NumberInputField />
        </NumberInput>
        <Button size="sm" variant="outline" onClick={()=>qc.invalidateQueries({ queryKey: ['admin-engagement-tx'] })}>Obnovit</Button>
      </HStack>
      <Box borderWidth="1px" borderRadius="md" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Uživatel</Th>
              <Th>Delta</Th>
              <Th>Důvod</Th>
              <Th>Meta</Th>
              <Th>Čas</Th>
            </Tr>
          </Thead>
          <Tbody>
            {(txQ.data || []).map((t: any) => (
              <Tr key={t.id}>
                <Td>#{t.id}</Td>
                <Td>
                  { (usersQ.data || []).find((u:any)=>u.id===t.user_id)?.name ? (
                    <HStack spacing={1}>
                      <Text noOfLines={1}>{(usersQ.data || []).find((u:any)=>u.id===t.user_id)?.name}</Text>
                      <Text color="gray.500" fontSize="xs">#{t.user_id}</Text>
                    </HStack>
                  ) : (
                    <Text>#{t.user_id}</Text>
                  )}
                </Td>
                <Td>{t.delta}</Td>
                <Td><Badge>{t.reason}</Badge></Td>
                <Td><Text fontSize="xs" noOfLines={1}>{t.meta ? JSON.stringify(t.meta) : '-'}</Text></Td>
                <Td>{t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Heading size="xs" mt={4}>Manuální úprava bodů</Heading>
      <VStack align="stretch" spacing={2}>
        <HStack flexWrap="wrap" rowGap={2}>
          <Select placeholder="Vyberte uživatele" value={adjUserId} onChange={(e)=>setAdjUserId(e.target.value)} maxW="260px">
            {(usersQ.data || []).map(u => (
              <option key={u.id} value={String(u.id)}>{u.name || u.email} (#{u.id})</option>
            ))}
          </Select>
          <Input placeholder="Delta (+/-)" value={adjDelta} onChange={(e)=>setAdjDelta(e.target.value)} maxW="160px" />
          <Select value={adjReason} onChange={(e)=>setAdjReason(e.target.value)} maxW="240px">
            <option value="admin_adjust">admin_adjust</option>
            <option value="bonus">bonus</option>
            <option value="penalty">penalty</option>
          </Select>
        </HStack>
        <Button colorScheme="blue" size="sm" onClick={()=>passwordModal.onOpen()} isLoading={adjustMut.isPending} isDisabled={!adjUserId || !adjDelta}>Upravit body</Button>
        {/* Password confirmation modal */}
        <Modal isOpen={passwordModal.isOpen} onClose={passwordModal.onClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Potvrzení úpravy bodů</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={3}>
                <Text>Potvrďte akci zadáním vašeho administračního hesla.</Text>
                <FormControl>
                  <FormLabel display="flex" alignItems="center" justifyContent="space-between">
                    <Box>Heslo administrátora</Box>
                    <PasswordHelpTooltip />
                  </FormLabel>
                  <Input type="password" value={currentPassword} onChange={(e)=>setCurrentPassword(e.target.value)} />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <HStack>
                <Button onClick={passwordModal.onClose}>Zrušit</Button>
                <Button colorScheme="blue" onClick={()=>adjustMut.mutate()} isLoading={adjustMut.isPending} isDisabled={!currentPassword.trim()}>Potvrdit</Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </VStack>
  );
};

export default EngagementAdminPage;
